import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { sendGA4, GA4 } from "../hooks/useAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingSuccessPage() {
  const { t } = useTranslation();
  const { checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const poll = async () => {
      if (pollCount.current >= 10) {
        setStatus("timeout");
        return;
      }
      pollCount.current++;
      try {
        const res = await axios.get(`${API}/stripe/checkout-status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          sendGA4(GA4.CHECKOUT_SUCCESS, { session_id: sessionId });
          await checkAuth();
          return;
        }
        if (res.data.status === "expired") {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (pollCount.current >= 3) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, checkAuth]);

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="billing-success-page">
      <Card className="max-w-md w-full border-slate-200 shadow-md">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="h-12 w-12 text-teal-600 animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">{t("billing.checking")}</h1>
              <p className="text-sm text-slate-500">{t("billing.processingDesc")}</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">{t("billing.successTitle")}</h1>
              <p className="text-sm text-slate-500 mb-6">{t("billing.successDesc")}</p>
              <Link to="/dashboard">
                <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="billing-go-dashboard">
                  {t("billing.goToDashboard")}
                </Button>
              </Link>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">{t("billing.errorTitle")}</h1>
              <p className="text-sm text-slate-500 mb-6">{t("billing.errorDesc")}</p>
              <Link to="/dashboard">
                <Button variant="outline" className="w-full" data-testid="billing-error-dashboard">
                  {t("billing.goToDashboard")}
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
